import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldCheck, Pencil, Eye, ToggleLeft, Search, Download } from "lucide-react";
import { downloadCsv } from "@/lib/csv";

const ACTION_META = {
  "user.update": { label: "User edit", icon: Pencil, color: "text-emerald-700 bg-emerald-50" },
  "user.impersonate": { label: "Impersonation", icon: Eye, color: "text-violet-700 bg-violet-50" },
  "feature_flags.update": { label: "Feature flag", icon: ToggleLeft, color: "text-amber-700 bg-amber-50" },
};

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "user.update", label: "User edits" },
  { value: "user.impersonate", label: "Impersonations" },
  { value: "feature_flags.update", label: "Feature flag changes" },
];

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [action, setAction] = useState("");
  const [emailQ, setEmailQ] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (action) params.action = action;
      if (emailQ) {
        // search either admin or target — server-side regex on target_email is a common case
        params.target_email = emailQ;
      }
      const r = await api.get("/admin/audit-log", { params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [action, emailQ]);

  useEffect(() => { refresh(); }, [refresh]);

  const onExport = async () => {
    // Pull the full filtered set (cap at 5k for safety) instead of just visible rows.
    try {
      const params = { limit: 5000 };
      if (action) params.action = action;
      if (emailQ) params.target_email = emailQ;
      const r = await api.get("/admin/audit-log", { params });
      const all = r.data.rows || [];
      const columns = [
        { label: "created_at", accessor: (r) => r.created_at },
        { label: "action", accessor: (r) => r.action },
        { label: "admin_email", accessor: (r) => r.admin_email },
        { label: "target_email", accessor: (r) => r.target_email || "" },
        { label: "before", accessor: (r) => r.before },
        { label: "after", accessor: (r) => r.after },
        { label: "metadata", accessor: (r) => r.metadata },
      ];
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`ispeedtoclose-audit-log-${stamp}.csv`, columns, all);
      toast.success(`Exported ${all.length} row${all.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div data-testid="admin-audit-log">
      <div className="card p-4 mb-4 flex flex-wrap gap-2 items-center">
        <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide mr-2">Filter</div>
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="input max-w-[220px]"
          data-testid="audit-filter-action"
        >
          {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-[280px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
          <input
            value={emailQ}
            onChange={(e) => setEmailQ(e.target.value)}
            placeholder="Target email contains…"
            className="input pl-9"
            data-testid="audit-filter-email"
          />
        </div>
        <button type="button" className="btn btn-ghost ml-auto" onClick={refresh} data-testid="audit-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
        <button type="button" className="btn btn-ghost" onClick={onExport} data-testid="audit-export-csv">
          <Download size={14} /> Export CSV
        </button>
        <div className="text-sm text-[var(--ink-2)] ml-2">
          <strong className="text-[var(--ink)]">{total}</strong>&nbsp;total
        </div>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin inline" /> Loading audit log…
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-8 text-center text-[var(--ink-2)]" data-testid="audit-empty">
          <ShieldCheck size={24} className="inline mb-2 text-emerald-600" />
          <div className="font-semibold">No matching activity</div>
          <div className="text-sm">Admin actions (PATCH user / toggle flag / impersonate) will appear here.</div>
        </div>
      ) : (
        <div className="card divide-y" data-testid="audit-list">
          {rows.map((row) => {
            const meta = ACTION_META[row.action] || {
              label: row.action, icon: Pencil, color: "text-slate-700 bg-slate-50",
            };
            const Icon = meta.icon;
            return (
              <div
                key={row.id}
                className="p-3 flex items-start gap-3"
                data-testid={`audit-row-${row.id}`}
              >
                <span className={"w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 " + meta.color}>
                  <Icon size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <span className="font-semibold text-violet-700 truncate max-w-[200px]" title={row.admin_email}>
                      {row.admin_email}
                    </span>
                    <span className="text-xs text-[var(--ink-3)] uppercase tracking-wide">{meta.label}</span>
                    {row.target_email && (
                      <>
                        <span className="text-[var(--ink-3)] text-xs">→</span>
                        <span className="font-mono text-xs truncate max-w-[220px]" title={row.target_email}>
                          {row.target_email}
                        </span>
                      </>
                    )}
                  </div>
                  <AuditDelta row={row} />
                </div>
                <div className="text-xs text-[var(--ink-3)] text-right whitespace-nowrap">
                  {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AuditDelta({ row }) {
  // user.update — diff before/after
  if (row.action === "user.update" && row.before && row.after) {
    const keys = Object.keys(row.after).filter((k) => JSON.stringify(row.before[k]) !== JSON.stringify(row.after[k]));
    if (keys.length === 0) return null;
    return (
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--ink-2)]">
        {keys.map((k) => (
          <span key={k}>
            <span className="font-semibold text-[var(--ink)]">{k}:</span>{" "}
            <span className="line-through text-rose-600">{fmt(row.before[k])}</span>{" "}
            → <span className="text-emerald-700">{fmt(row.after[k])}</span>
          </span>
        ))}
      </div>
    );
  }
  // feature_flags.update — render changes map
  if (row.action === "feature_flags.update") {
    const changes = row.metadata?.changes || {};
    const keys = Object.keys(changes);
    if (keys.length === 0) return null;
    return (
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--ink-2)]">
        {keys.map((k) => (
          <span key={k}>
            <span className="font-semibold text-[var(--ink)]">{k}:</span>{" "}
            <span className="line-through text-rose-600">{fmt(changes[k].before)}</span>{" "}
            → <span className="text-emerald-700">{fmt(changes[k].after)}</span>
          </span>
        ))}
      </div>
    );
  }
  // impersonate — session ttl
  if (row.action === "user.impersonate") {
    return (
      <div className="mt-1 text-xs text-[var(--ink-2)]">
        {row.metadata?.ttl_minutes || 30}-min session
      </div>
    );
  }
  return null;
}

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (v === true) return "on";
  if (v === false) return "off";
  if (v === "") return "—";
  return String(v);
}
